import { ArrowLeft, Cookie } from 'lucide-react'

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <a
            href="/"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </a>
          <div className="flex items-center gap-3">
            <Cookie className="w-8 h-8 text-primary-600" />
            <h1 className="text-3xl font-bold text-gray-900">Cookie Policy</h1>
          </div>
          <p className="text-gray-600 mt-2">Last updated: April 11, 2026</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-md p-8 space-y-8">

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">What Are Cookies?</h2>
            <p className="text-gray-700 leading-relaxed">
              Cookies are small text files stored on your device when you visit our website. They help
              us provide you with a better experience by remembering your preferences and understanding
              how you use our Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We use cookies for the following purposes:</p>

            <div className="space-y-6">
              <div className="bg-green-50 border-l-4 border-green-500 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">1. Essential Cookies (Required)</h3>
                <p className="text-gray-700 text-sm mb-2">
                  These cookies are necessary for the website to function and cannot be disabled.
                </p>
                <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                  <li>Authentication and session management</li>
                  <li>Security and fraud prevention</li>
                  <li>Cookie consent preferences</li>
                </ul>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2. Functional Cookies (Optional)</h3>
                <p className="text-gray-700 text-sm mb-2">
                  These cookies enable enhanced functionality and personalization.
                </p>
                <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                  <li>Remember your location preferences</li>
                  <li>Save your language settings</li>
                  <li>Store delivery addresses</li>
                </ul>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-500 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">3. Analytics Cookies (Optional)</h3>
                <p className="text-gray-700 text-sm mb-2">
                  These cookies help us understand how visitors use our website.
                </p>
                <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                  <li>Google Analytics (anonymized)</li>
                  <li>Page views and user interactions</li>
                  <li>Performance monitoring</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">4. Marketing Cookies (Optional)</h3>
                <p className="text-gray-700 text-sm mb-2">
                  These cookies are used to deliver relevant advertisements.
                </p>
                <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                  <li>Track campaign effectiveness</li>
                  <li>Personalized marketing messages</li>
                  <li>Social media integration</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies We Use</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-900">Cookie Name</th>
                    <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-900">Purpose</th>
                    <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                    <th className="border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-900">Duration</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  <tr>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">nearbies_session</td>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">Authentication</td>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">Essential</td>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">Session</td>
                  </tr>
                  <tr>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">nearbies_cookie_consent</td>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">Cookie preferences</td>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">Essential</td>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">1 year</td>
                  </tr>
                  <tr>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">nearbies_location</td>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">Remember location</td>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">Functional</td>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">30 days</td>
                  </tr>
                  <tr>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">_ga, _gid</td>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">Google Analytics</td>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">Analytics</td>
                    <td className="border-b px-4 py-3 text-sm text-gray-700">2 years / 24 hours</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Managing Your Cookie Preferences</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You can control and manage cookies in several ways:
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">On Our Website</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you first visit our site, you'll see a cookie consent banner. You can:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Accept all cookies</li>
              <li>Decline optional cookies (only essential cookies will be used)</li>
              <li>Change your preferences at any time by clicking "Cookie Settings" in the footer</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">In Your Browser</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Most browsers allow you to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>View and delete cookies</li>
              <li>Block third-party cookies</li>
              <li>Block cookies from specific websites</li>
              <li>Delete all cookies when you close the browser</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>Note:</strong> Blocking all cookies may prevent you from using certain features of our website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use services from third parties that may set their own cookies:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Google Analytics:</strong> Web traffic analysis (anonymized)</li>
              <li><strong>Payment Processors:</strong> Secure payment processing</li>
              <li><strong>Social Media:</strong> Social sharing buttons</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              These third parties have their own privacy policies. We recommend reviewing them.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Updates to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Cookie Policy from time to time. Changes will be posted on this page
              with an updated "Last updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions about our use of cookies:
              </p>
              <ul className="text-gray-700 space-y-2">
                <li><strong>Email:</strong> <a href="mailto:privacy@nearbies.co.uk" className="text-primary-600 underline">privacy@nearbies.co.uk</a></li>
                <li><strong>Subject:</strong> "Cookie Policy Inquiry"</li>
              </ul>
            </div>
          </section>

        </div>
      </main>
    </div>
  )
}

export default CookiePolicy
